import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, getDefaultRoute, useAuth } from "./context/AuthContext";
import LoginPage from "./pages/LoginPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import CompanyAdminPage from "./pages/CompanyAdminPage";
import EmployeeProfilePage from "./pages/EmployeeProfilePage";
import KioskLivePage from "./pages/KioskLivePage";
import ManagerPage from "./pages/ManagerPage";
import EmployeePage from "./pages/EmployeePage";
import ScanPage from "./pages/ScanPage";
import ScannerAdminPage from "./pages/ScannerAdminPage";
import ReportsPage from "./pages/ReportsPage";
import "./App.css";


function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FAF8F3]" data-testid="app-loading-screen">
      <div className="rounded-3xl border border-[#0D1B2A]/10 bg-white px-8 py-6 text-sm font-semibold text-[#0D1B2A]/70 shadow-sm" data-testid="app-loading-text">
        Loading Tarka HR...
      </div>
    </div>
  );
}


function ProtectedRoute({ allowedRoles, children }) {
  const location = useLocation();
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`} replace />;
  }
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to={getDefaultRoute(user)} replace />;
  }
  return children;
}


function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  return <Navigate to={user ? getDefaultRoute(user) : "/login"} replace />;
}


function AppRoutes() {
  return (
    <>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<LoginPage />} />

        <Route path="/platform/dashboard" element={<ProtectedRoute allowedRoles={["super_admin"]}><SuperAdminPage section="dashboard" /></ProtectedRoute>} />
        <Route path="/platform/tenants" element={<ProtectedRoute allowedRoles={["super_admin"]}><SuperAdminPage section="tenants" /></ProtectedRoute>} />
        <Route path="/platform/audit" element={<ProtectedRoute allowedRoles={["super_admin"]}><SuperAdminPage section="audit" /></ProtectedRoute>} />

        <Route path="/company/dashboard" element={<ProtectedRoute allowedRoles={["company_admin"]}><CompanyAdminPage section="dashboard" /></ProtectedRoute>} />
        <Route path="/company/employees" element={<ProtectedRoute allowedRoles={["company_admin"]}><CompanyAdminPage section="employees" /></ProtectedRoute>} />
        <Route path="/company/employees/:employeeId" element={<ProtectedRoute allowedRoles={["company_admin"]}><EmployeeProfilePage /></ProtectedRoute>} />
        <Route path="/company/attendance" element={<ProtectedRoute allowedRoles={["company_admin"]}><CompanyAdminPage section="attendance" /></ProtectedRoute>} />
        <Route path="/company/approvals" element={<ProtectedRoute allowedRoles={["company_admin"]}><CompanyAdminPage section="approvals" /></ProtectedRoute>} />
        <Route path="/company/kiosk" element={<ProtectedRoute allowedRoles={["company_admin"]}><CompanyAdminPage section="kiosk" /></ProtectedRoute>} />
        <Route path="/company/kiosk/live/:sessionId" element={<ProtectedRoute allowedRoles={["company_admin"]}><KioskLivePage /></ProtectedRoute>} />
        <Route path="/kiosk/live/:sessionId" element={<KioskLivePage />} />
        <Route path="/company/reports" element={<ProtectedRoute allowedRoles={["company_admin"]}><ReportsPage /></ProtectedRoute>} />
        <Route path="/company/leave-settings" element={<ProtectedRoute allowedRoles={["company_admin"]}><CompanyAdminPage section="leave-settings" /></ProtectedRoute>} />
        <Route path="/company/settings" element={<ProtectedRoute allowedRoles={["company_admin"]}><CompanyAdminPage section="settings" /></ProtectedRoute>} />

        <Route path="/scanner/scan" element={<ProtectedRoute allowedRoles={["scanner_admin"]}><ScannerAdminPage /></ProtectedRoute>} />

        <Route path="/manager/dashboard" element={<ProtectedRoute allowedRoles={["manager"]}><ManagerPage section="dashboard" /></ProtectedRoute>} />
        <Route path="/manager/team" element={<ProtectedRoute allowedRoles={["manager"]}><ManagerPage section="team" /></ProtectedRoute>} />
        <Route path="/manager/approvals" element={<ProtectedRoute allowedRoles={["manager"]}><ManagerPage section="approvals" /></ProtectedRoute>} />
        <Route path="/manager/reports" element={<ProtectedRoute allowedRoles={["manager"]}><ReportsPage /></ProtectedRoute>} />

        <Route path="/employee/dashboard" element={<ProtectedRoute allowedRoles={["employee", "manager"]}><EmployeePage section="dashboard" /></ProtectedRoute>} />
        <Route path="/employee/attendance" element={<ProtectedRoute allowedRoles={["employee", "manager"]}><EmployeePage section="attendance" /></ProtectedRoute>} />
        <Route path="/employee/leave" element={<ProtectedRoute allowedRoles={["employee", "manager"]}><EmployeePage section="leave" /></ProtectedRoute>} />
        <Route path="/employee/regularization" element={<ProtectedRoute allowedRoles={["employee", "manager"]}><EmployeePage section="regularization" /></ProtectedRoute>} />
        <Route path="/employee/scan" element={<ProtectedRoute allowedRoles={["employee", "manager"]}><EmployeePage section="scan" /></ProtectedRoute>} />

        <Route path="/scan/:token" element={<ScanPage />} />
      </Routes>

      <Toaster richColors closeButton position="top-right" />
    </>
  );
}


export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}
